import { differenceInMinutes } from 'date-fns'
import { INQUIRY_ITEM } from '@/types'

export const formatCurrency = (amountInCents: string | number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amountInCents) / 100)
}
export const parseCurrency = (formattedAmount: string) => {
  const cleanedInput = formattedAmount.replace(/[^0-9.-]+/g, '')
  let number = parseFloat(cleanedInput)
  number = Math.round(number * 100) / 100
  // Vraća broj, ne string
  return number
}

// https://stackoverflow.com/questions/48172772/time-zone-issue-involving-date-fns-format
export function normalizeTimeZone(value: string) {
  value = value.replace(/[+-]\d{2}:\d{2}$/, 'Z')

  const dt = new Date(value)
  return new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000)
}

export function pluralizeWord(word: string, count?: number) {
  if (!/^[a-z]+$/i.test(word)) {
    return word
  }

  const plural = (
    word.replace(/(?:s|x|z|ch|sh)$/i, '$&e').replace(/([^aeiou])y$/i, '$1ie') +
    's'
  ).replace(/i?e?s$/i, (match) => {
    const isTailLowerCase = word.slice(-1) === word.slice(-1).toLowerCase()
    return isTailLowerCase ? match.toLowerCase() : match.toUpperCase()
  })

  return typeof count === 'number' && Math.abs(count) === 1 ? word : plural
}
export function generateTime(start: string, end: string, step: number) {
  const startTime = parseTime(start)
  let endTime = parseTime(end)

  if (endTime < startTime) {
    endTime = addMinutes(endTime, 1440) //add day if end time is less than start time
  }

  function generateTimes(currentTime: Date, accumulator: Date[]) {
    if (currentTime >= endTime) {
      return accumulator
    }
    return generateTimes(
      addMinutes(currentTime, step),
      accumulator.concat(currentTime),
    )
  }

  return generateTimes(startTime, [])
}

export function parseTime(timeString: string) {
  const [hours, minutes] = timeString.split(':').map(Number)
  return new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate(),
    hours,
    minutes,
  )
}

export function formatTime(time: Date) {
  return time.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function addMinutes(time: Date, minutes: number) {
  return new Date(time.getTime() + minutes * 60000)
}

export function setDuration(startTime: Date, endTime: Date) {
  let duration = differenceInMinutes(endTime, startTime)

  return duration < 0 ? (duration += 60 * 24) : duration
}

export function formatTimeForRequest(datepicker: Date, startTime: Date) {
  return {
    year: datepicker.getFullYear(),
    month: datepicker.getMonth() + 1,
    day: datepicker.getDate(),
    hour: startTime.getHours(),
    min: startTime.getMinutes(),
  }
}

export function formatPhoneNumber(phoneNumberString: string) {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '')
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    const intlCode = match[1] ? '+1 ' : ''
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
  }
  return phoneNumberString
}

export function formatDetailsPhoneNumber(phoneNumberString: string) {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '')
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return ['(', match[2], ') ', match[3], '-', match[4]].join('')
  }
  return phoneNumberString
}

export const clearPhoneNumber = (number: string) => {
  if (number) {
    let formattedNumber = number.replace(/[()\-\s]/g, '')
    if (formattedNumber.startsWith('00')) {
      formattedNumber = `+${formattedNumber.substring(2)}`
    }
    if (formattedNumber.startsWith('1') && formattedNumber.length === 11) {
      formattedNumber = `+${formattedNumber}`
    }
    if (!formattedNumber.startsWith('+')) {
      formattedNumber = `+1${formattedNumber}`
    }
    return formattedNumber
  }
  return number
}

export function singularize(word: string) {
  const endings: { [key: string]: string } = {
    ves: 'fe',
    ies: 'y',
    i: 'us',
    zes: 'ze',
    ses: 's',
    es: 'e',
    s: '',
  }

  return word
    .split(/\b/)
    .map((w) =>
      w.replace(
        new RegExp(`(${Object.keys(endings).join('|')})$`),
        (r) => endings[r],
      ),
    )
    .join('')
}

export function convertToTitleCase(str: string) {
  const noUnderscores = str.replace(/_/g, ' ')
  const withSpaces = noUnderscores.replace(/([A-Z])/g, ' $1').trim()
  const titleCase = withSpaces
    .toLowerCase()
    .replace(/(^|\s)\S/g, (L) => L.toUpperCase())
  return titleCase
}

export function capitalize(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}

export function toSentenceCase(text: string) {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function remapDetailFieldNames(fieldName: string) {
  let updatedFieldName = ''
  switch (fieldName) {
    case 'budget':
      updatedFieldName = 'budget per person'
      break;
    case 'how_did_you_hear': 
      updatedFieldName = 'how did you hear about us?'
      break;
    case 'how_did_you_hear_details':
        updatedFieldName = 'how did you hear about us?'
      break;
    default:
      updatedFieldName = fieldName.replaceAll('_', ' ')
  }
  return updatedFieldName
}

export function formatCommentText(text: string) {
  var v1 = text.split('__READ_MORE_PLACEHOLDER__')[0]
  var v2 = text.split('<br>').slice(0, 5).join('<br>')
  var result = v1.length <= v2.length ? v1 : v2
  return result
}

export function splitIntoArrayOfWords(s: string) {
  s = s.replace(/(^\s*)|(\s*$)/gi, '') //exclude  start and end white-space
  s = s.replace(/[ ]{2,}/gi, ' ') //2 or more space to 1
  s = s.replace(/\n /, '\n') // exclude newline with a start spacing

  return s.split(' ').filter(function (str) {
    return str != ''
  })
}

export const parseCommentText = (text: string, truncate = false) => {
  let commentLength = 15
  let initialText = text.split('__READ_MORE_PLACEHOLDER__')[0].trim()
  initialText = formatCommentText(initialText)
  const wordsArray = splitIntoArrayOfWords(initialText)

  if (truncate && wordsArray.length > commentLength) {
    initialText = wordsArray.slice(0, commentLength).join(' ')
  }

  const urlRegex = /https?:\/\/\S+/g
  const parts = []
  let lastIndex = 0

  // Using a for loop with exec for better readability and control
  for (let match; (match = urlRegex.exec(initialText)) !== null; ) {
    const textPart = initialText.slice(lastIndex, match.index)
    // Only add text parts that are non-empty
    if (textPart) {
      parts.push({ type: 'text', content: textPart })
    }

    // Add the URL as a link part
    parts.push({ type: 'link', content: match[0] })
    lastIndex = match.index + match[0].length
  }

  // Add any remaining text after the last URL
  if (lastIndex < initialText.length) {
    parts.push({ type: 'text', content: initialText.slice(lastIndex) })
  }

  return parts
}

export function paginateRenderCondition({
  currentPage,
  pageCount,
  item,
  condition,
}: {
  currentPage: number
  pageCount: number
  item: number
  condition: 'hidden' | 'dots'
}) {  
  return condition === 'hidden'
    ? ((currentPage > 4 &&
        currentPage < pageCount - 3 &&
        (currentPage < item - 2 || currentPage > item + 2)) ||
        (currentPage < 5 && item > 6) ||
        (currentPage > pageCount - 4 && item < pageCount - 5)) &&
        item != 1 &&
        item != pageCount &&
        pageCount > 7
    : ((currentPage > 4 &&
        currentPage < pageCount - 3 &&
        (currentPage === item + 2 || currentPage === item - 2)) ||
        (currentPage < 5 && item === 6) ||
        (currentPage > pageCount - 4 && item === pageCount - 5)) &&
        item != 1 &&
        item != pageCount &&
        pageCount > 7
}

export function sanitizeFileName(fileName: string) {
  return fileName
    .replace(/[^a-zA-Z0-9.\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export const getShortName = (name: string, maxLength = 18) => {
  if (name.length <= maxLength) return name

  const sliced = name.slice(0, maxLength).trimEnd()
  const lastSpace = sliced.lastIndexOf(' ')

  if (lastSpace > 0) {
    return sliced.slice(0, lastSpace)
  }

  return sliced
}

const PROMOTION_SOURCE_LABELS: Record<string, string> = {
  contactless_menu: 'Contactless Menu',
  email: 'Email Newsletter',
  footer: 'Website Footer',
  google: 'Google',
  header: 'Website Header',
  pop_up: 'Website PopUp',
  printed_menu: 'Printed Menu',
  qr_code_flyer: 'QR Code',
  social: 'Social Media Post',
  texting: 'Texting Campaign',
  virtualhost: 'Virtual Host',
}

export function getInquirySource(inquiry: INQUIRY_ITEM): string {
  if (inquiry.source === 'voice' || inquiry.source === 'v_host')
    return 'Via Virtual Host'
  if (inquiry.promotion_source && PROMOTION_SOURCE_LABELS[inquiry.promotion_source])
    return `Via ${PROMOTION_SOURCE_LABELS[inquiry.promotion_source]}`
  return ''
}

export function getInquiryDetailsSource(inquiry: INQUIRY_ITEM): string {
  if (inquiry.source === 'voice') return 'Virtual Host - In Call'
  if (inquiry.source === 'v_host' || inquiry.promotion_source === 'virtualhost') return 'Virtual Host - Text Message'
  if (inquiry.promotion_source && PROMOTION_SOURCE_LABELS[inquiry.promotion_source])
    return PROMOTION_SOURCE_LABELS[inquiry.promotion_source]
  return ''
}


